import api from './api';
import ErrorHandler from './errorHandler';

/**
 * Tag API Service
 */
class TagService {
    /**
     * Get all tags
     * @param {Object} params - Query parameters
     * @param {string} params.search - Search query
     * @param {string} params.sortBy - Sort field
     * @param {string} params.sortOrder - Sort order (asc/desc)
     * @returns {Promise<Array>} List of tags
     */
    async getAllTags(params = {}) {
        try {
            const response = await api.get('/api/tags', { params });
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Get tag by ID
     * @param {number} id - Tag ID
     * @returns {Promise<Object>} Tag details
     */
    async getTagById(id) {
        try {
            const response = await api.get(`/api/tags/${id}`);
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Get tag statistics (admin only)
     * @returns {Promise<Object>} Tag statistics
     */
    async getTagStats() {
        try {
            const response = await api.get('/api/tags/stats');
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Create new tag (admin only)
     * @param {Object} data - Tag data
     * @param {string} data.name - Tag name
     * @param {string} data.slug - Tag slug
     * @param {string} data.description - Tag description
     * @returns {Promise<Object>} Created tag
     */
    async createTag(data) {
        try {
            const response = await api.post('/api/tags', data);
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Update tag (admin only)
     * @param {number} id - Tag ID
     * @param {Object} data - Updated tag data
     * @returns {Promise<Object>} Updated tag
     */
    async updateTag(id, data) {
        try {
            const response = await api.put(`/api/tags/${id}`, data);
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Delete tag (admin only)
     * @param {number} id - Tag ID
     * @returns {Promise<Object>} Deletion confirmation
     */
    async deleteTag(id) {
        try {
            const response = await api.delete(`/api/tags/${id}`);
            return response; // This is already the backend's response object
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

}

export default new TagService();
